import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { visit } from "unist-util-visit";

/** Extensão controlada: ++texto++ vira sublinhado, interpretada igual no editor, na prévia e no app. */
const remarkUnderline = () => (tree) => {
  visit(tree, "text", (node, index, parent) => {
    if (!parent || typeof node.value !== "string" || !node.value.includes("++")) return;
    const parts = node.value.split(/\+\+(.+?)\+\+/gs);
    if (parts.length === 1) return;
    const children = parts.map((part, i) =>
      i % 2 === 1
        ? { type: "emphasis", data: { hName: "u" }, children: [{ type: "text", value: part }] }
        : { type: "text", value: part }
    );
    parent.children.splice(index, 1, ...children.filter((c) => c.type !== "text" || c.value !== ""));
    return index + children.length;
  });
};

const ALLOWED = [
  "p",
  "strong",
  "em",
  "u",
  "h1",
  "h2",
  "h3",
  "h4",
  "ul",
  "ol",
  "li",
  "a",
  "blockquote",
  "br",
  "hr",
  "code",
  "pre",
  "del",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
];

const components = {
  h1: ({ node, ...props }) => <h1 className="mt-6 font-display text-2xl font-medium tracking-tight text-ink-900" {...props} />,
  h2: ({ node, ...props }) => <h2 className="mt-6 font-display text-lg font-medium text-ink-900 md:text-lg" {...props} />,
  h3: ({ node, ...props }) => <h3 className="mt-5 font-display text-base font-semibold text-ink-800" {...props} />,
  h4: ({ node, ...props }) => <h4 className="mt-4 text-sm font-semibold text-ink-800" {...props} />,
  p: ({ node, ...props }) => <p className="mt-3 text-[15px] leading-relaxed text-ink-600" {...props} />,
  ul: ({ node, ...props }) => <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[15px] leading-relaxed text-ink-600" {...props} />,
  ol: ({ node, ...props }) => <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-[15px] leading-relaxed text-ink-600" {...props} />,
  li: ({ node, ...props }) => <li className="pl-1" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-ink-900" {...props} />,
  u: ({ node, ...props }) => <u className="underline decoration-sage-600/60 underline-offset-2" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="mt-4 border-l-2 border-sage-600/50 bg-sand/40 px-4 py-2 text-[15px] italic leading-relaxed text-ink-600" {...props} />
  ),
  a: ({ node, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="font-medium text-sage-700 underline underline-offset-2"
    />
  ),
  hr: () => <hr className="my-6 border-border" />,
  code: ({ node, ...props }) => <code className="rounded bg-sand px-1.5 py-0.5 text-[13px] text-ink-800" {...props} />,
};

/** Renderização segura: sem HTML bruto, sem script e sem esquema de link perigoso. */
export const Markdown = ({ children, testId = "markdown-body", className = "" }) => (
  <div data-testid={testId} className={`er-markdown ${className}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkUnderline]}
      allowedElements={ALLOWED}
      unwrapDisallowed
      components={components}
    >
      {children || ""}
    </ReactMarkdown>
  </div>
);

export default Markdown;
